import { useHistory } from 'react-router-dom';
import { useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { styled, darken } from '@mui/material/styles';

/* Styles */
import styles from 'styles/login.module.scss';
import layoutStyles from 'styles/layout.module.scss';

/* i18n */
import { useIntl, FormattedMessage } from 'react-intl';
import { getUrlPrefix } from 'i18n/config';

/* API */
import { matchClaimantAccount } from 'lib/API';
import { useMutation } from '@tanstack/react-query';

/* Material UI and other UI Dependencies */
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  FilledInput,
  FormHelperText,
  IconButton,
  Alert,
  Button,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import OnboardingSteps from 'components/OnboardingSteps';
import DateInput from 'components/forms/DateInput';
import SSNInput from 'components/inputs/FullSSNInput';
import { preventSubmitOnEnter } from 'lib/utils';
import OnboardingContentWrapper from 'components/OnboardingContentWrapper';

export const WhiteFilledInput = styled(FilledInput)(() => ({
  '.MuiFilledInput-input': { borderRadius: '4px 4px 0 0 !important' },
  '.MuiFilledInput-input:not(.Mui-disabled):not(:hover)': { backgroundColor: 'white' },
}));

/**
 * Converts a date in "MM/DD/YYYY" format to "YYYYMMDD" format.
 */
export function convertDate(dob) {
  const [month, day, year] = dob.split('/');
  return `${year}${month}${day}`;
}

export default function OnboardingUIClaimant() {
  const intl = useIntl();
  const history = useHistory();
  const urlPrefix = getUrlPrefix(intl.locale);

  const [ssnType, setSsnType] = useState('password');
  const [submitting, setSubmitting] = useState(false);

  const UIClaimantFormValidationSchema = yup.object({
    dob: yup
      .date('forms.onboarding.dateOfBirthValidation')
      .typeError('forms.onboarding.dateOfBirthInvalid')
      .min('01/01/1900', 'forms.onboarding.dateOfBirthTooOld')
      .max(new Date(), 'forms.onboarding.dateOfBirthTooNew')
      .required('forms.onboarding.dateOfBirthRequired'),
    ssn: yup
      .string('forms.onboarding.fullSocialSecurityNumber')
      .matches(/^[0-9]+$/, 'forms.onboarding.onlyDigits')
      .min(9, 'forms.onboarding.exactlyXDigits')
      .max(9, 'forms.onboarding.exactlyXDigits')
      .required('forms.onboarding.ssnRequired'),
  });

  const matchClaimantAccountMutation = useMutation({ mutationFn: (values) => matchClaimantAccount(values) });

  const formik = useFormik({
    initialValues: {
      ssn: '',
      dob: '',
    },
    validationSchema: UIClaimantFormValidationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        await matchClaimantAccountMutation.mutateAsync({
          ssn: values.ssn.replace(/\D/g, ''),
          dob: convertDate(values.dob),
        });
        setSubmitting(false);
        history.push(`${urlPrefix}/onboarding/goals`);
      } catch (_error) {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box sx={{ boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.1)' }}>
      <div className={styles.loginFlow__form__onboarding}>
        <Box px={3}>
          <Typography align="center" variant="h1" gutterBottom sx={{ fontSize: 24, mt: 1 }}>
            {intl.formatMessage({ id: 'onboarding.title' }, { step: 5, totalSteps: 5 })}
          </Typography>
        </Box>
        <Box sx={{ maxWidth: 222, marginLeft: 'auto', marginRight: 'auto', mt: 4, mb: 1 }}>
          <OnboardingSteps activeStep={5} />
        </Box>
      </div>
      <hr className={layoutStyles.separatorBold} />

      <Box className={styles.loadingScreen__cardsContainer}>
        <OnboardingContentWrapper>
          <form id="onboarding-ui-claimant-form" onSubmit={formik.handleSubmit}>
            <Typography variant="caption" mb={2} display="block">
              {intl.formatMessage({ id: 'forms.onboarding.uiClaimant.body1' })}
            </Typography>

            <Typography variant="caption" display="block">
              {intl.formatMessage({ id: 'forms.onboarding.uiClaimant.body1a' })}
            </Typography>

            <Box component="ul" sx={{ my: 1, mb: 2 }}>
              <Typography component="li" variant="caption">
                {intl.formatMessage({ id: 'forms.onboarding.uiClaimant.bullet1' })}
              </Typography>
              <Typography component="li" variant="caption">
                {intl.formatMessage({ id: 'forms.onboarding.uiClaimant.bullet2' })}
              </Typography>
              <Typography component="li" variant="caption">
                {intl.formatMessage({ id: 'forms.onboarding.uiClaimant.bullet3' })}
              </Typography>
            </Box>

            <Typography variant="caption" mb={2} display="block">
              {intl.formatMessage({ id: 'forms.onboarding.uiClaimant.body2' })}
            </Typography>

            <Box sx={{ position: 'relative', mb: 2 }}>
              <FormControl error={formik.touched.ssn && Boolean(formik.errors.ssn)} fullWidth>
                <InputLabel htmlFor="social-security-ui-claimant" variant="filled">
                  {intl.formatMessage({ id: 'forms.onboarding.fullSocialSecurityNumber' })}
                </InputLabel>
                <SSNInput
                  name="ssn"
                  type={ssnType}
                  customInput={WhiteFilledInput}
                  fullWidth
                  autoComplete="off"
                  label={intl.formatMessage({ id: 'forms.onboarding.fullSocialSecurityNumber' })}
                  value={formik.values.ssn}
                  onChange={formik.handleChange}
                  onKeyDown={preventSubmitOnEnter}
                  inputProps={{
                    title: intl.formatMessage({ id: 'forms.onboarding.fullSocialSecurityNumber' }),
                  }}
                  id="social-security-ui-claimant"
                />
                {formik.touched.ssn && formik.errors.ssn && (
                  <FormHelperText>{intl.formatMessage({ id: formik.errors.ssn }, { amount: 9 })}</FormHelperText>
                )}
              </FormControl>

              <IconButton
                title={intl.formatMessage({ id: 'forms.onboarding.fullSocialSecurityNumber' })}
                style={{ position: 'absolute', right: '0', top: '0' }}
                onClick={() => {
                  setSsnType(ssnType === 'password' ? 'text' : 'password');
                }}
                size="large"
              >
                {ssnType === 'text' ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </Box>

            <FormControl error={formik.touched.dob && Boolean(formik.errors.dob)} fullWidth>
              <InputLabel htmlFor="dob-ui-claimant" variant="filled">
                {intl.formatMessage({ id: 'forms.onboarding.dateOfBirth' })}
              </InputLabel>
              <DateInput
                name="dob"
                customInput={WhiteFilledInput}
                fullWidth
                autoComplete="off"
                value={formik.values.dob}
                onChange={formik.handleChange}
                onKeyDown={preventSubmitOnEnter}
                onBlur={formik.handleBlur}
                id="dob-ui-claimant"
              />
              {formik.touched.dob && formik.errors.dob && (
                <FormHelperText>{intl.formatMessage({ id: formik.errors.dob })}</FormHelperText>
              )}
            </FormControl>

            {matchClaimantAccountMutation.isError && (
              <Alert sx={{ mt: 1.5 }} severity="error">
                {(() => {
                  const errorCode = matchClaimantAccountMutation.error?.response?.data?.detail?.error_code || null;
                  return (
                    <FormattedMessage
                      id="forms.onboarding.uiClaimant.defaultAlertError"
                      values={{
                        errorCode: errorCode ? `${errorCode}: ` : '',
                        p: (chunks) => <Typography>{chunks}</Typography>,
                        link: (chunks) => (
                          <Typography
                            component="a"
                            href="https://sas.arkansas.gov/state-technology/ardata/launch-support-request-forms/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: (theme) => darken(theme.palette.info.main, 0.1),
                              textDecoration: 'underline',
                              fontWeight: 700,
                              '&:hover': { color: (theme) => darken(theme.palette.info.main, 0.2) },
                            }}
                          >
                            {chunks}
                          </Typography>
                        ),
                      }}
                    />
                  );
                })()}
              </Alert>
            )}
          </form>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              color="secondary"
              size="large"
              variant="outlined"
              onClick={() => history.push(`${urlPrefix}/onboarding/location`)}
              disabled={submitting}
              id="back-ui-claimant-button"
              sx={{
                borderColor: 'secondary.main',
                color: 'secondary.main',
                backgroundColor: '#fff',
                '&:hover': { borderColor: 'secondary.dark', backgroundColor: '#f0f0f0' },
              }}
            >
              {intl.formatMessage({ id: 'forms.onboarding.back' })}
            </Button>
            <Button
              color="secondary"
              size="large"
              variant="outlined"
              onClick={() => history.push(`${urlPrefix}/onboarding/goals`)}
              disabled={submitting}
              id="skip-ui-claimant-button"
            >
              {intl.formatMessage({ id: 'forms.onboarding.skipForNow' })}
            </Button>
            <Button
              color="secondary"
              size="large"
              variant="contained"
              onClick={() => {
                const formElement = document.getElementById('onboarding-ui-claimant-form');
                if (formElement) formElement.requestSubmit();
              }}
              disabled={submitting}
              id="next-ui-claimant-button"
            >
              {intl.formatMessage({ id: 'experience.form.save' })}
            </Button>
          </Box>
        </OnboardingContentWrapper>
      </Box>
    </Box>
  );
}
